import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

import { useConfig } from "@jti/app-context/useConfig";
import { Separator } from "@jti/core-components/components/Separator";
import { BREAKPOINT_SMALL_LARGE } from "@jti/fe-common/constants";
import { areBundleOptionsFromCart, ICartItemErrorType, type Product } from "@jti/magento";
import { usePdpLink } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { OutOfStock, Price, QuantityInput, Tooltip, useTranslations } from "@jti/ui";

import { ConsentItemFlag } from "@checkout/components/CartItem/ItemFlag";
import { usePromotionalOffers } from "@checkout/hooks/usePromotionalOffers";
import { hasCartErrorCode } from "@checkout/utils/getCartErrors";

import { RemoveItemDialog } from "../../RemoveItemDialog";
import { CartLimitError } from "../CartLimitError";
import { SubscriptionItemFlag } from "../ItemFlag/SubscriptionItemFlag";
import { ProductDescription } from "../ProductDescription";
import { ProductPoints } from "../ProductPoints";
import { RegionBadge } from "../RegionBadge";
import { TradeInSelect } from "../RegularItem/TradeInSelect";
import { RemoveCartItemButton } from "../RemoveCartItemButton";
import { computeQuantityOrderLimit } from "../utils/computeQuantityOrderLimit";

import BundleOption from "./BundleOption";

import styles from "./BundleItem.module.scss";

export type BundleItemProps = {
  item: Product;
  qtyPending?: boolean;
  blockQuantityAdd?: boolean;
  onRemoveClick(id: string | undefined): void;
  onQuantityChange(id: string | undefined, quantity: number): void;
};

export const BundleItem: FC<BundleItemProps> = ({
  qtyPending = false,
  item,
  blockQuantityAdd = false,
  onRemoveClick,
  onQuantityChange,
}) => {
  const { __ } = useTranslations();
  const isScreenAboveMobile = useMediaQuery({ minWidth: BREAKPOINT_SMALL_LARGE });
  const [isOpenRemoveDialog, setIsOpenRemoveDialog] = useState(false);
  const {
    id,
    sku,
    name,
    quantity,
    price,
    description,
    status,
    points,
    enable_order_quantity_limit,
    order_quantity_limit,
    tooltip_info,
    promotionalOffers,
    tradeIn,
    has_registration_consent,
  } = item;
  const [limitMessageStatus, setLimitMessageStatus] = useState(false);
  const [isQuantityLimitReached, setIsQuantityLimitReached] = useState(false);
  const grandTotalPrice = useSelector((state) => state.cart.prices.data?.grandTotal.value) ?? 0;
  const shippingPrice = useSelector((state) => state.cart.prices.data?.shipping?.value) ?? 0;
  const maxOrderLimit = useSelector((state) => state.cart.storeConfig?.order_limit_value);
  const orderLimitEnabled = useSelector((state) => state.cart.storeConfig?.is_order_limit_enabled);
  const { SHOP_URL, PDP_URL } = useConfig();
  const pdpLink = usePdpLink(sku, { shopUrl: SHOP_URL, pdpUrl: PDP_URL ?? "" });
  const isOutOfStock =
    (item.errors && hasCartErrorCode(item.errors, ICartItemErrorType.ItemQty)) || status === "OUT_OF_STOCK";
  const isCartonsLimitReached =
    (item.errors && hasCartErrorCode(item.errors, ICartItemErrorType.CartonsLimit)) || false;
  const {
    isLimitReached: isPromotionalOfferLimitReached,
    hasPromotionalOffers,
    promotionalOfferLimit,
  } = usePromotionalOffers(quantity, promotionalOffers);
  const isSubscriptionItem = item.subscription === 1;
  const quantityOrderLimit = computeQuantityOrderLimit({
    hasPromotionalOffers,
    promotionalOfferLimit,
    tradeIn,
    enable_order_quantity_limit,
    order_quantity_limit,
  });

  useEffect(() => {
    const skuLimitReached = quantity >= order_quantity_limit && enable_order_quantity_limit === 1;
    const specialOfferLimitReached = hasPromotionalOffers && isPromotionalOfferLimitReached;

    setIsQuantityLimitReached(skuLimitReached || specialOfferLimitReached);
  }, [
    quantity,
    hasPromotionalOffers,
    isPromotionalOfferLimitReached,
    enable_order_quantity_limit,
    order_quantity_limit,
  ]);

  useEffect(() => {
    setLimitMessageStatus(grandTotalPrice + price.value / quantity - shippingPrice > maxOrderLimit);
  }, [grandTotalPrice, maxOrderLimit, price, quantity, shippingPrice]);

  const showRemoveItemDialog = useCallback(() => {
    setIsOpenRemoveDialog(true);
  }, [setIsOpenRemoveDialog]);

  const handleOnCancel = useCallback(() => {
    setIsOpenRemoveDialog(false);
  }, [setIsOpenRemoveDialog]);

  const handleOnRemove = useCallback(() => {
    onRemoveClick(id);
    handleOnCancel();
  }, [id, onRemoveClick, handleOnCancel]);

  const handleQuantityChange = useCallback(
    (value: number) => {
      onQuantityChange(id, value);
    },
    [id, onQuantityChange]
  );

  const isAddDisabled = blockQuantityAdd || isQuantityLimitReached;

  const renderTitle = (_name: string) => (
    <strong data-testid="bundleItemName" className={styles.bundleName}>
      {_name}
    </strong>
  );

  return (
    <div className={styles.bundleItem} data-testid="bundleItem">
      <div className={styles.rows}>
        <div className={styles.product}>
          <div className={styles.productName}>
            {!!item.delivery_region_code && <RegionBadge regionCode={item.delivery_region_code} isBundleItem={true} />}
            {pdpLink ? <a href={pdpLink}>{renderTitle(name)}</a> : renderTitle(name)}
            {tooltip_info && <Tooltip content={<span>{tooltip_info}</span>} />}
            {isOutOfStock && <OutOfStock hideIcon={true} />}
            <div className={styles.productAttributes}>
              {isSubscriptionItem ? <SubscriptionItemFlag className={styles.subscriptionFlag} /> : null}
              {has_registration_consent && <ConsentItemFlag />}
            </div>
          </div>
        </div>
        <div className={styles.priceSection}>
          <Price
            price={price}
            className={styles.price}
            frequency={isSubscriptionItem ? __("cart.cart_item.subscription_billing_frequency") : undefined}
          />
        </div>
        <div className={styles.sideways}>
          <div className={styles.actions}>
            <RemoveCartItemButton onClick={showRemoveItemDialog} />
          </div>
          <Price
            price={price}
            className={styles.price}
            frequency={isSubscriptionItem ? __("cart.cart_item.subscription_billing_frequency") : undefined}
          />
        </div>

        {tradeIn && (
          <div className={styles.tradeInSelect}>
            <TradeInSelect sku={sku} />
          </div>
        )}

        <div className={styles.separator}>
          <Separator />
        </div>
        <div className={styles.cardFooterDescription}>
          <ProductPoints pointsQuantity={points || 0} />
          <ProductDescription description={description} visibleOn="all" />
        </div>

        <div className={styles.quantity}>
          <div className={styles.quantityInputContainer}>
            <QuantityInput
              align={isScreenAboveMobile ? "end" : "start"}
              value={quantity}
              disabled={status === "OUT_OF_STOCK" || qtyPending}
              disabledAdd={isAddDisabled}
              max={quantityOrderLimit.isLimit === 1 ? quantityOrderLimit.quantityLimit : undefined}
              title={status === "OUT_OF_STOCK" ? __("cart.cart_item.out_of_stock") : undefined}
              onChange={handleQuantityChange}
              orderLimitEnabled={orderLimitEnabled}
              isOrderLimitReached={limitMessageStatus}
              maxOrderLimit={maxOrderLimit}
              currency={price.currency}
            />
          </div>
        </div>
      </div>

      <CartLimitError
        className={styles.marginBottom}
        isCartonsLimitReached={isCartonsLimitReached}
        isQuantityLimitReached={isQuantityLimitReached}
        quantityLimit={quantityOrderLimit.quantityLimit}
      />

      <ul className={styles.bundleOptionsContainer}>
        {item?.bundle_options?.map(
          ({ label, uid, values }) =>
            values &&
            areBundleOptionsFromCart(values) && (
              <BundleOption key={`${sku} - ${label} - ${uid}}`} categoryName={label} values={values} />
            )
        )}
      </ul>

      <RemoveItemDialog
        item={item}
        isOpenDialog={isOpenRemoveDialog}
        onRemove={handleOnRemove}
        onCancel={handleOnCancel}
      />
    </div>
  );
};
